import type {ConnectorId} from '@cohort/shared/schema/apps';
import {isCohortError} from '@cohort/shared-frontend/common/errors';
import type {App} from '@cohort/wallet/apps';
import {useAppConnection} from '@cohort/wallet/hooks/api/Apps';
import {campaignsKeys} from '@cohort/wallet/hooks/api/Campaign';
import {useCohortMutation} from '@cohort/wallet/hooks/api/Query';
import {spacesKeys} from '@cohort/wallet/hooks/api/Space';
import {userAttributesKeys} from '@cohort/wallet/hooks/api/UserAttributes';
import useNotify from '@cohort/wallet/hooks/notify';
import {useApi} from '@cohort/wallet/hooks/useApi';
import {JourneyParticipationsApi} from '@cohort/wallet/lib/Endpoints';
import {getJourneyDetailRoute} from '@cohort/wallet/lib/Pages';
import type {ConnectionWDto} from '@cohort/wallet-schemas/connections';
import type {JourneyStepTriggerWDto} from '@cohort/wallet-schemas/journeySteps';
import type {VerificationAttemptInput} from '@cohort/wallet-schemas/journeyStepTriggers';
import {useQueryClient} from '@tanstack/react-query';
import {useTranslation} from 'react-i18next';
import {useNavigate} from 'react-router-dom';

type Output = {
  processStepVerification: (verificationAttemptInput?: VerificationAttemptInput) => void;
  isVerificationLoading: boolean;
  isConnectionFetching: boolean;
  userConnection: ConnectionWDto | null;
};

export const useVerifyStep = (
  trigger: JourneyStepTriggerWDto,
  app: App,
  journeyId: string,
  journeyParticipationId: string | undefined,
  journeyStepId: string,
  merchantId: string
): Output => {
  const {t} = useTranslation('apps', {keyPrefix: 'useStepVerification'});
  const notify = useNotify();
  const triggerCanBeVerified =
    trigger.verificationAttemptsLeft === null || trigger.verificationAttemptsLeft > 0;
  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
  const shouldFetchConnection = app.spec.userConnector !== null && triggerCanBeVerified;
  const verifyStep = useApi(JourneyParticipationsApi.verifyStep);
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const {data: userConnection, isFetching: isConnectionFetching} = useAppConnection(
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    app.spec.userConnector?.id as ConnectorId,
    shouldFetchConnection
  );

  const {mutate: processStepVerification, isLoading: isVerificationLoading} = useCohortMutation({
    mutationFn: async (verificationAttemptInput: VerificationAttemptInput) =>
      verifyStep(journeyParticipationId ?? '', {
        journeyStepId: journeyStepId,
        journeyStepTriggerId: trigger.id,
        userConnectionId: userConnection?.id || null,
        verificationAttemptInput,
      }),
    onSettled: () =>
      queryClient.invalidateQueries(campaignsKeys.getJourneyById(merchantId, journeyId)),
    onError: err => {
      // @Devs, using navigate() to reset the navigation state.
      navigate(getJourneyDetailRoute(journeyId), {
        state: {processingStepId: undefined},
        replace: true,
      });
      const errMessage = isCohortError(err, 'journey-step-completion.verification-failed')
        ? t('verificationFailedNotification')
        : t('verificationErrorNotification');
      return notify('error', errMessage);
    },
    onSuccess: () => {
      queryClient.invalidateQueries(spacesKeys.getSpace());
      // some trigger completion can update user attributes
      queryClient.invalidateQueries(userAttributesKeys.userAttributes);
    },
  });

  return {
    processStepVerification,
    isVerificationLoading,
    isConnectionFetching,
    userConnection: userConnection ?? null,
  };
};
